import { useEffect, useRef, useState } from 'preact/hooks';

import { cssSelector, describeElement } from '../capture/selector';
import { t } from '../i18n';
import { Icon } from './icons';

import type { Rect, Selection } from '../types';

interface PickerProps {
  onPick: (selection: Selection) => void;
  onCancel: () => void;
  /** Inline style that keeps the picking layer off the docked sidebar. */
  layerStyle: Record<string, string | number>;
}

const toRect = (r: DOMRect): Rect => ({ x: r.left, y: r.top, width: r.width, height: r.height });
const scroll = () => ({ x: window.scrollX, y: window.scrollY });
/** Touch screens get a bottom bar and tap-to-select, tap-again-to-confirm. */
const isTouch = () => window.matchMedia('(pointer: coarse)').matches;

function Banner({
  icon,
  text,
  sub,
  onCancel,
  touch,
}: {
  icon: 'element' | 'area';
  text: string;
  sub?: string;
  onCancel: () => void;
  touch: boolean;
}) {
  const s = t();
  return (
    <div class={`hfb-banner${touch ? ' is-sheet' : ''}`} role="status">
      <Icon name={icon} size={18} class="hfb-banner-icon" />
      <span class="hfb-banner-text">
        <strong>{text}</strong>
        {sub && <span>{sub}</span>}
      </span>
      {!touch && (
        <span class="hfb-banner-hint">
          <kbd>{s.esc}</kbd> {s.toCancel}
        </span>
      )}
      <button
        type="button"
        class="hfb-btn hfb-btn-outline hfb-btn-small hfb-banner-cancel"
        onClick={onCancel}
      >
        {s.cancel}
      </button>
    </div>
  );
}

function useLatest<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

function useEscape(onCancel: () => void) {
  const cancel = useLatest(onCancel);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      cancel.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);
}

/** "button Save changes" → tag and text, for the label next to the outline. */
function splitDescription(description: string): { tag: string; text: string } {
  const match = /^(\S+)\s*(?:"(.*)")?$/.exec(description);
  return { tag: match?.[1] ?? description, text: match?.[2] ?? '' };
}

/**
 * Hover (or a first tap on touch screens) outlines the element under the pointer; a click (or a
 * second tap on the same element) selects it. Events are swallowed in the capture phase on
 * window, so the host app never sees the selecting click.
 */
export function ElementPicker({
  host,
  onPick,
  onCancel,
  layerStyle,
}: PickerProps & { host: Element }) {
  const s = t();
  const touch = isTouch();
  const [hover, setHover] = useState<{ rect: Rect; label: string } | null>(null);
  const pick = useLatest(onPick);
  useEscape(onCancel);

  useEffect(() => {
    let current: Element | null = null;
    let armed: Element | null = null;
    const inWidget = (event: Event) => event.composedPath().includes(host);
    const targetOf = (event: Event) => {
      const target = event.composedPath()[0];
      return target instanceof Element ? target : null;
    };
    const update = () => {
      setHover(
        current
          ? { rect: toRect(current.getBoundingClientRect()), label: describeElement(current) }
          : null,
      );
    };

    const onMove = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      current = inWidget(event) ? null : targetOf(event);
      update();
    };
    const block = (event: Event) => {
      if (inWidget(event)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    const select = (el: Element) =>
      pick.current({
        kind: 'element',
        selector: cssSelector(el),
        description: describeElement(el),
        rect: toRect(el.getBoundingClientRect()),
        scroll: scroll(),
      });
    const onClick = (event: MouseEvent) => {
      if (inWidget(event)) return;
      block(event);
      const el = targetOf(event);
      if (!el) return;
      if (touch && armed !== el) {
        // First tap: show what would be picked. A second tap on it confirms.
        armed = el;
        current = el;
        update();
        return;
      }
      select(el);
    };

    const blocked = [
      'pointerdown',
      'pointerup',
      'mousedown',
      'mouseup',
      'dblclick',
      'contextmenu',
      'touchstart',
      'touchend',
    ];
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('click', onClick, true);
    window.addEventListener('scroll', update, true);
    for (const type of blocked)
      window.addEventListener(type, block, { capture: true, passive: false });
    const previousCursor = document.documentElement.style.cursor;
    document.documentElement.style.cursor = 'crosshair';

    return () => {
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('click', onClick, true);
      window.removeEventListener('scroll', update, true);
      for (const type of blocked) window.removeEventListener(type, block, true);
      document.documentElement.style.cursor = previousCursor;
    };
  }, [host]);

  const label = hover ? splitDescription(hover.label) : null;
  return (
    <div class="hfb-layer hfb-layer-passive" style={layerStyle}>
      {hover && label && (
        <div
          class="hfb-highlight"
          style={{
            left: hover.rect.x - 3,
            top: hover.rect.y - 3,
            width: hover.rect.width + 6,
            height: hover.rect.height + 6,
          }}
        >
          <span class={`hfb-tag${hover.rect.y < 40 ? ' is-below' : ''}`}>
            <span class="hfb-tag-dot" aria-hidden="true" />
            <code>{label.tag}</code>
            {label.text && <span>{label.text}</span>}
          </span>
        </div>
      )}
      <Banner
        icon="element"
        touch={touch}
        text={touch ? s.bannerTapElement : s.bannerElement}
        sub={touch ? s.bannerTapAgain : undefined}
        onCancel={onCancel}
      />
    </div>
  );
}

const MIN_AREA = 8;

export function AreaPicker({ onPick, onCancel, layerStyle }: PickerProps) {
  const s = t();
  const touch = isTouch();
  const [start, setStart] = useState<{ x: number; y: number } | null>(null);
  const [end, setEnd] = useState<{ x: number; y: number } | null>(null);
  const layer = useRef<HTMLDivElement>(null);
  useEscape(onCancel);

  // The layer itself starts at the sidebar's edge on the left; convert to viewport coordinates.
  const rect: Rect | null =
    start && end
      ? {
          x: Math.min(start.x, end.x),
          y: Math.min(start.y, end.y),
          width: Math.abs(end.x - start.x),
          height: Math.abs(end.y - start.y),
        }
      : null;
  const offset = layer.current?.getBoundingClientRect() ?? { left: 0, top: 0 };

  return (
    <div
      ref={layer}
      class="hfb-layer hfb-area-layer"
      style={layerStyle}
      onPointerDown={(event) => {
        if ((event.target as Element).closest('.hfb-banner')) return;
        try {
          (event.currentTarget as Element).setPointerCapture(event.pointerId);
        } catch {
          // Pointer no longer active; the drag still works while the pointer stays on the layer.
        }
        setStart({ x: event.clientX, y: event.clientY });
        setEnd({ x: event.clientX, y: event.clientY });
      }}
      onPointerMove={(event) => {
        if (start) setEnd({ x: event.clientX, y: event.clientY });
      }}
      onPointerUp={() => {
        const selected = rect;
        setStart(null);
        setEnd(null);
        if (selected && selected.width >= MIN_AREA && selected.height >= MIN_AREA) {
          onPick({ kind: 'area', rect: selected, scroll: scroll() });
        }
      }}
    >
      {rect ? (
        <>
          <div
            class="hfb-area-rect"
            style={{
              left: rect.x - offset.left,
              top: rect.y - offset.top,
              width: rect.width,
              height: rect.height,
            }}
          />
          <span
            class="hfb-size-tag"
            style={{ left: rect.x - offset.left, top: rect.y - offset.top + rect.height + 8 }}
          >
            {Math.round(rect.width)} × {Math.round(rect.height)}
          </span>
        </>
      ) : (
        <div class="hfb-area-dim" />
      )}
      <Banner
        icon="area"
        touch={touch}
        text={touch ? s.bannerTapArea : s.bannerArea}
        onCancel={onCancel}
      />
    </div>
  );
}
